import React, { useEffect, useRef } from 'react';

/**
 * Ambient canvas: a drifting field of nodes that links up when close enough,
 * with packets travelling along the strongest links. Purely decorative.
 */
const NodeField = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const ctx = canvas.getContext('2d');
    let width = 0;
    let height = 0;
    let dpr = 1;
    let frame;
    let nodes = [];
    let packets = [];

    const LINK_DISTANCE = 150;

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const density = Math.round((width * height) / 24000);
      const count = Math.max(18, Math.min(64, density));

      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.22,
        vy: (Math.random() - 0.5) * 0.22,
        r: Math.random() * 1.4 + 0.9,
      }));

      packets = [];
    };

    const spawnPacket = () => {
      if (nodes.length < 2 || packets.length > 6) return;
      const a = Math.floor(Math.random() * nodes.length);
      let b = Math.floor(Math.random() * nodes.length);
      if (a === b) b = (b + 1) % nodes.length;
      packets.push({ a, b, t: 0, speed: 0.004 + Math.random() * 0.005 });
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      // links
      for (let i = 0; i < nodes.length; i += 1) {
        const n = nodes[i];
        n.x += n.vx;
        n.y += n.vy;

        if (n.x < 0 || n.x > width) n.vx *= -1;
        if (n.y < 0 || n.y > height) n.vy *= -1;

        for (let j = i + 1; j < nodes.length; j += 1) {
          const m = nodes[j];
          const dx = n.x - m.x;
          const dy = n.y - m.y;
          const dist = Math.hypot(dx, dy);
          if (dist > LINK_DISTANCE) continue;

          const alpha = (1 - dist / LINK_DISTANCE) * 0.3;
          ctx.strokeStyle = `rgba(129, 140, 248, ${alpha})`;
          ctx.lineWidth = 0.7;
          ctx.beginPath();
          ctx.moveTo(n.x, n.y);
          ctx.lineTo(m.x, m.y);
          ctx.stroke();
        }
      }

      // nodes
      nodes.forEach((n) => {
        ctx.fillStyle = 'rgba(94, 234, 212, 0.55)';
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fill();
      });

      // packets travelling between node pairs
      packets = packets.filter((p) => {
        const from = nodes[p.a];
        const to = nodes[p.b];
        if (!from || !to) return false;

        p.t += p.speed;
        if (p.t >= 1) return false;

        const x = from.x + (to.x - from.x) * p.t;
        const y = from.y + (to.y - from.y) * p.t;
        const fade = Math.sin(p.t * Math.PI);

        ctx.fillStyle = `rgba(192, 132, 252, ${fade * 0.9})`;
        ctx.beginPath();
        ctx.arc(x, y, 1.8, 0, Math.PI * 2);
        ctx.fill();

        ctx.strokeStyle = `rgba(192, 132, 252, ${fade * 0.25})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(x, y);
        ctx.stroke();

        return true;
      });

      if (Math.random() < 0.02) spawnPacket();

      frame = window.requestAnimationFrame(draw);
    };

    resize();

    if (reduceMotion) {
      // Render a single static frame instead of animating.
      nodes.forEach((n) => {
        n.vx = 0;
        n.vy = 0;
      });
      draw();
      window.cancelAnimationFrame(frame);
      frame = undefined;
    } else {
      frame = window.requestAnimationFrame(draw);
    }

    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return <canvas ref={canvasRef} className="hero__canvas" aria-hidden="true" />;
};

export default NodeField;
